import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../common/Reveal";
import { articleImgFallback, onImgError } from "../../lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_ORDER = ["ms", "en", "zh", "ta", "id"];
const LANG_LABELS = { ms: "BM", en: "EN", zh: "中文", ta: "தமிழ்", id: "ID" };

export const BlogList = ({ vertical, channel, basePath, limit, testId }) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [langFilter, setLangFilter] = useState("");

  useEffect(() => {
    fetch(`${API}/content?vertical=${vertical}${channel ? `&channel=${channel}` : ""}`)
      .then((r) => r.json())
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [vertical, channel]);

  const langs = LANG_ORDER.filter((c) => items.some((a) => a.language === c));
  const activeLang = langFilter || (langs.includes("ms") ? "ms" : langs[0] || "");
  const shown = langs.length > 1 ? items.filter((a) => a.language === activeLang) : items;
  const list = limit ? shown.slice(0, limit) : shown;

  if (loaded && list.length === 0) {
    return (
      <p className="mt-8 border-l-2 border-amber pl-6 font-serif text-lg italic text-ink/60" data-testid={`${testId}-empty`}>
        The first pieces are being written. Published articles will appear here.
      </p>
    );
  }

  return (
    <>
      {langs.length > 1 && (
        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid={`${testId}-lang-toggle`}>
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/45">Translate:</span>
          {langs.map((code) => (
            <button
              key={code}
              onClick={() => setLangFilter(code)}
              data-testid={`${testId}-lang-${code}`}
              className={`px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] transition-colors duration-200 ${activeLang === code ? "bg-ink text-warmwhite" : "border border-ink/15 text-ink/60 hover:border-amber hover:text-ink"}`}
            >
              {LANG_LABELS[code] || code}
            </button>
          ))}
        </div>
      )}
      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid={testId}>
      {list.map((a, i) => (
        <Reveal key={a.id} delay={i * 0.08} className="h-full">
          <Link
            to={`${basePath}/${a.slug}`}
            data-testid={`${testId}-card-${a.slug}`}
            className="group flex h-full flex-col overflow-hidden border border-ink/10 bg-white transition-shadow duration-300 hover:shadow-xl"
          >
              <div className="h-44 shrink-0 overflow-hidden">
                <img src={a.featured_image || articleImgFallback(a.vertical)} alt={a.alt_text || a.title} loading="lazy" onError={onImgError(a.vertical)} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            <div className="flex flex-1 flex-col p-7">
            {a.category && (
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber">{a.category}</p>
            )}
            <h3 className="mt-3 font-serif text-xl leading-snug text-ink">{a.title}</h3>
            {a.excerpt && <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/65 line-clamp-3">{a.excerpt}</p>}
            <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-ink/45">
              Written by {a.author || "Dr. Irma"}
              {a.publish_date ? ` · ${a.publish_date}` : ""}
            </p>
            <span className="link-arrow mt-5">
              Read <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </span>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
    </>
  );
};
